import { Injectable } from '@angular/core';
import { Pupil, StudentAccount, Teacher, Profile } from '@df/interfaces';
import { HttpClient } from '@angular/common/http';
import { MAIN_DOMAIN, API_PATH } from '@app/app.constants';
import { Observable, Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ResponseProfile, ResponseUser, ResponseGroups, ResponseMain } from '@app/_interfaces/responses';
import { APIService } from '@app/_services/api.service';

@Injectable({
    providedIn: 'root'
})
export class AccountService {
    private CURRENT_GROUP_ID = 'CURRENT_GROUP_ID';
    private NEW_EMAIL = 'NEW_EMAIL';
    private students: StudentAccount[] = [];

    $currentTeacher: Observable<Teacher>;
    $currentUser: Observable<ResponseUser>;
    currentGroupId: number;

    studentSelected = new Subject<StudentAccount>();
    studentsUpdated = new Subject<StudentAccount[]>();
    studentUpdated = new Subject<number>();
    profile = new Subject<Profile>();
    currentGroupInstance: any;

    constructor(private http: HttpClient, private api: APIService) {}

    setCurrentGroupId(groupId): void {
        localStorage.setItem(this.CURRENT_GROUP_ID, groupId);
    }

    private get getCurrentGroupId(): number {
        return +localStorage.getItem(this.CURRENT_GROUP_ID);
    }

    getCurrentUser(): Observable<ResponseUser> {
        return this.api.get(API_PATH.user);
    }

    getProfileData(groupId?: number): Observable<ResponseProfile> {
        const getCurrentGroupId = groupId || this.getCurrentGroupId;
        const url = `${API_PATH.people.groups}${getCurrentGroupId}/get_profile`;
        return this.api.get(url);
    }

    patchProfile(profile: Profile): Observable<ResponseProfile> {
        return this.api.patch(API_PATH.people.patch_profile, profile);
    }

    patchAvatarProfile(formData: any): Observable<ResponseProfile> {
        return this.api.patch(API_PATH.people.patch_profile_avatar, formData);
    }

    changeEmail(profile: Profile): Observable<ResponseProfile> {
        return this.api.post(API_PATH.people.change_email, profile);
    }

    getCurrentGroupInstance(): Observable<ResponseMain> {
        return this.api.get(API_PATH.people.current_group_instance);
    }

    set setCurrentGroupInstance(instance: any) {
        this.currentGroupInstance = instance;
    }

    saveNewEmail(newEmail: string): boolean {
        localStorage.setItem(this.NEW_EMAIL, newEmail);
        return true;
    }

    getUser(): Observable<ResponseUser> {
        if (!this.$currentUser) {
            this.$currentUser = this.getCurrentUser().pipe(shareReplay(1));
        }
        return this.$currentUser;
    }

    getUserGroups(username: string): Observable<ResponseGroups> {
        const url = `people/${username}/groups/`;
        return this.api.get(url);
    }

    public getCurrentTeacher(): Observable<Teacher> {
        if (!this.$currentTeacher) {
            this.$currentTeacher = this.getCurrentTeacherData().pipe(shareReplay(1));
        }

        return this.$currentTeacher;
    }

    clearTeacher() {
        this.$currentTeacher = null;
    }

    private getCurrentTeacherData(): Observable<Teacher> {
        return this.http.get<Teacher>(`${MAIN_DOMAIN}/api/people/teacher`);
    }

    public setStudents(students: StudentAccount[]): void {
        this.students = students;
        this.studentsUpdated.next(this.students.slice());
    }

    getStudentAccount(studentId: number): Observable<any> {
        return this.api.get(`accounts/account/student/${studentId}/`);
    }

    sendConfirmEmail(userId): Observable<any> {
        return this.api.get(`accounts/confirm_email/${userId}/`);
    }

    public accountOperation(student: Pupil, data: any): Observable<any> {
        return this.http.post<any>(`${MAIN_DOMAIN}/api/accounts/account/student/${student.id}/`, data);
    }

    public getClassAccounts(classID: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/accounts/students_class/${classID}/`);
    }

    public updateStudentAccount(wantedStudent: StudentAccount) {
        const index = this.students.findIndex(student => student.id === wantedStudent.id);
        if (index !== -1) {
            this.students[index] = wantedStudent;
            this.studentsUpdated.next(this.students.slice());
            this.studentUpdated.next(index);
        }
    }

    public getTransactions(account: number, page: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/accounts/account/transactions/?page=${page}&account=${account}`);
    }

    public getNewTransactions(userId: number, page: number, reward_active: string): Observable<any> {
        return this.http.get<any>(
            `${MAIN_DOMAIN}/api/accounts/new_transactions/?page=${page}&user=${userId}&amount_type=${reward_active}`
        );
    }

    getClassReward(classID: number): Observable<any> {
        return this.http.get<any>(`${MAIN_DOMAIN}/api/accounts/class_reward/${classID}/`);
    }

    deactivateUser(userId: number): Observable<any> {
        return this.api.get(`accounts/deactivate_user/${userId}/`);
    }

    changePassword(email: string): Observable<any> {
        return this.api.post(`${API_PATH.accounts.reset_password}`, { email });
    }
}
